import { type ReactNode } from "react";

export interface TabItem<K extends string = string> {
  key: K;
  label: string;
  badge?: string | number;
  content: ReactNode;
}

interface TabsProps<K extends string> {
  items: TabItem<K>[];
  active: K;
  onChange: (key: K) => void;
}

export function Tabs<K extends string>({
  items,
  active,
  onChange,
}: TabsProps<K>): JSX.Element {
  const activeItem = items.find((it) => it.key === active) ?? items[0];

  return (
    <div>
      <div role="tablist" className="flex gap-1 border-b border-slate-200">
        {items.map((it) => {
          const isActive = it.key === active;
          return (
            <button
              key={it.key}
              role="tab"
              aria-selected={isActive}
              aria-controls={`tabpanel-${it.key}`}
              id={`tab-${it.key}`}
              type="button"
              onClick={() => onChange(it.key)}
              className={[
                "px-4 py-2 text-sm font-medium -mb-px border-b-2 transition-colors",
                isActive
                  ? "border-brand-600 text-brand-700"
                  : "border-transparent text-slate-500 hover:text-slate-900",
              ].join(" ")}
            >
              {it.label}
              {it.badge !== undefined && (
                <span className="ml-1 text-xs text-slate-400">({it.badge})</span>
              )}
            </button>
          );
        })}
      </div>
      <div
        role="tabpanel"
        id={`tabpanel-${activeItem.key}`}
        aria-labelledby={`tab-${activeItem.key}`}
        className="pt-4"
      >
        {activeItem.content}
      </div>
    </div>
  );
}
